import {
  COACH_PROFILE_SECTIONS,
  CoachProfileEditor,
  CoachProfileSectionNav,
  coachProfileSectionSpy,
  formatProfilePhotoCommitError,
  hasPhotoEditChanges,
  markCoachProfileDirty,
  type ProfilePhotoDraft,
  type ProfilePhotoEditSession,
  pruneEmptyCoachProfileFragments,
  validateCoachProfile,
} from '@efdigital/ll-addon-coaching';
import type PicklistsService from '@efdigital/ll-addon-content/services/picklists';
import type { CoachProfile } from '@efdigital/ll-addon-data/types/coaching/coach-profile';
import type { EditingContext } from '@efdigital/ll-admin-v2/components/edit-context.gts';
import { EditRecordSidebar } from '@efdigital/ll-admin-v2/components/edit-record-sidebar.gts';
import { Validation } from '@efdigital/ll-admin-v2/components/validation.gts';
import {
  COACHING_COUNTRIES_GROUP,
  COACHING_INDUSTRIES_GROUP,
  COACHING_SENIORITY_GROUP,
  COACHING_SPECIALISMS_GROUP,
  COACHING_TYPES_GROUP,
} from '@efdigital/ll-admin-v2/data/builders/xceed/coach-profiles';
import { deepRollback } from '@efdigital/ll-admin-v2/data/deep-rollback';
import { deepSave } from '@efdigital/ll-admin-v2/data/deep-save';
import { StaysEditableAfterError } from '@efdigital/ll-admin-v2/data/stays-editable-after-error';
import type RouterService from '@ember/routing/router-service';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Store } from '@warp-drive/core';

type SavePhase = 'idle' | 'uploading-photo' | 'saving';

interface EditCoachSignature {
  Args: {
    coach: CoachProfile;
    context: EditingContext;
    canEdit: boolean;
    editLabel: string;
    saveLabel?: string;
    initialState?: 'readonly' | 'edit';
    isCustomSection: boolean;
    hasMovedToSection: () => void;
  };
  Blocks: {
    sidebar?: [];
    customSectionNav?: [
      buttonClass: string,
      inactiveClass: string,
      activeClass: string,
    ];
    customSectionView?: [
      isDisabled: boolean,
      photoDraft: ProfilePhotoDraft | null,
    ];
  };
  Element: HTMLFormElement;
}

const DEFAULT_SECTION_ID = COACH_PROFILE_SECTIONS[0]?.id ?? 'identity';

export class EditCoach extends Component<EditCoachSignature> {
  @service declare store: Store;
  @service declare router: RouterService;
  @service declare picklists: PicklistsService;
  @tracked activeSection = DEFAULT_SECTION_ID;
  @tracked savePhase: SavePhase = 'idle';
  @tracked photoDraft: ProfilePhotoDraft = null;
  @tracked photoEditSession: ProfilePhotoEditSession | null = null;

  setPhotoDraft = (draft: ProfilePhotoDraft): void => {
    this.photoDraft = draft;
  };

  setPhotoEditSession = (session: ProfilePhotoEditSession | null): void => {
    this.photoEditSession = session;
  };

  // Set while a click-triggered smooth-scroll is in flight, so the section
  // spy doesn't fight it and flicker through intermediate sections.
  #suppressSpyUntil = 0;
  #resumeSpyTimeout: ReturnType<typeof setTimeout> | null = null;

  // Picklist options come from the picklists service (the public, cached
  // /end-user/picklists/group-items endpoint) — no read:picklists right needed.
  get specialtyOptions() {
    return this.picklists.groups[COACHING_SPECIALISMS_GROUP]?.items ?? [];
  }

  // locations stays free text (omitted) — the editor falls back to tag entry.
  get positioningOptions() {
    return {
      countries: this.picklists.groups[COACHING_COUNTRIES_GROUP]?.items ?? [],
      coachingTypes: this.picklists.groups[COACHING_TYPES_GROUP]?.items ?? [],
      industries: this.picklists.groups[COACHING_INDUSTRIES_GROUP]?.items ?? [],
      levelsOfSeniority:
        this.picklists.groups[COACHING_SENIORITY_GROUP]?.items ?? [],
    };
  }

  get dynamicSaveLabel(): string {
    if (this.savePhase === 'uploading-photo') {
      return 'Uploading photo…';
    }

    if (this.savePhase === 'saving') {
      return 'Saving…';
    }

    return this.args.saveLabel ?? 'Save';
  }

  hasPhotoEditChanges = (): boolean => {
    return (
      hasPhotoEditChanges(this.photoDraft) ||
      (this.photoEditSession?.hasChanges() ?? false)
    );
  };

  saveCoach = async () => {
    try {
      if (this.photoEditSession?.hasChanges()) {
        this.savePhase = 'uploading-photo';
        try {
          await this.photoEditSession.commit(this.args.coach);
        } catch (error) {
          throw new StaysEditableAfterError(
            formatProfilePhotoCommitError(error),
            { cause: error }
          );
        }
      }

      this.savePhase = 'saving';
      pruneEmptyCoachProfileFragments(this.args.coach);
      markCoachProfileDirty(this.args.coach);
      await deepSave(this.args.coach, this.store, { maxDepth: 1 });
    } finally {
      this.savePhase = 'idle';
    }
  };

  revertCoach = () => {
    this.photoEditSession?.reset();
    this.photoDraft = null;
    deepRollback(this.args.coach, this.store, { maxDepth: 1 });
  };

  validateCoach = (coach: CoachProfile): Validation => {
    pruneEmptyCoachProfileFragments(coach);

    const failures = validateCoachProfile(coach, { mode: 'admin' });
    const firstFailure = failures[0];

    if (firstFailure) {
      const target = document.getElementById(
        `coach-section-${firstFailure.sectionId}`
      );

      if (target instanceof HTMLElement) {
        this.activeSection = firstFailure.sectionId;
        target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }

    const validation = firstFailure
      ? Validation.fromFailures(failures)
      : Validation.success();

    return validation;
  };

  scrollToSection = (sectionId: string, event: Event) => {
    event.preventDefault();
    this.#scrollToSection(sectionId);
  };

  #scrollToSection = (sectionId: string) => {
    this.activeSection = sectionId;
    this.args.hasMovedToSection();
    this.#pauseSpyDuringScroll();

    /* eslint-disable-next-line ember/no-runloop */
    next(() => {
      document
        .getElementById(`coach-section-${sectionId}`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  };

  /**
   * Suppresses the auto-highlight while a click-triggered smooth-scroll is
   * in flight, so it doesn't briefly highlight sections the scroll passes
   * through on the way to the clicked one. Resumes on the native
   * `scrollend` event where supported, with a flat timeout fallback.
   */
  #pauseSpyDuringScroll = () => {
    this.#suppressSpyUntil = Date.now() + 800;

    if (this.#resumeSpyTimeout) {
      clearTimeout(this.#resumeSpyTimeout);
    }

    this.#resumeSpyTimeout = setTimeout(() => {
      this.#suppressSpyUntil = 0;
    }, 800);

    if ('onscrollend' in window) {
      window.addEventListener(
        'scrollend',
        () => {
          this.#suppressSpyUntil = 0;

          if (this.#resumeSpyTimeout) {
            clearTimeout(this.#resumeSpyTimeout);
            this.#resumeSpyTimeout = null;
          }
        },
        { once: true }
      );
    }
  };

  handleSpyActiveSection = (sectionId: string) => {
    if (Date.now() < this.#suppressSpyUntil) {
      return;
    }

    this.activeSection = sectionId;
  };

  get isPublished(): boolean {
    return this.args.coach.status === 'published';
  }

  <template>
    <EditRecordSidebar
      id="coach-edit-form"
      @editLabel={{@editLabel}}
      @saveLabel={{this.dynamicSaveLabel}}
      @item={{@coach}}
      @context={{@context}}
      @maxDepth={{1}}
      @save={{this.saveCoach}}
      @revert={{this.revertCoach}}
      @validate={{this.validateCoach}}
      @hasUnsavedChanges={{this.hasPhotoEditChanges}}
      @canEdit={{@canEdit}}
      @initialState={{@initialState}}
      ...attributes
    >
      <:sidebar>
        {{yield to="sidebar"}}

        <h1 class="h5 fw-bold">
          {{#if this.isPublished}}
            Published version
          {{else}}
            Approval required
          {{/if}}
        </h1>

        <hr class="mt-2 mb-3" />

        <CoachProfileSectionNav
          @activeSection={{unless @isCustomSection this.activeSection}}
          @onSelect={{this.scrollToSection}}
          @firstSectionClass="mt-3"
          as |buttonClass inactiveClass activeClass|
        >
          {{yield buttonClass inactiveClass activeClass to="customSectionNav"}}
        </CoachProfileSectionNav>

        <hr class="my-3" />
      </:sidebar>
      <:form as |_uid isDisabled|>
        {{#if @isCustomSection}}
          {{yield isDisabled this.photoDraft to="customSectionView"}}
        {{/if}}
        {{! Keep the editor mounted while preview/compare is shown so staged photo edits are not lost. }}
        <div
          class="pt-3 {{if @isCustomSection 'd-none'}}"
          {{coachProfileSectionSpy this.handleSpyActiveSection}}
        >
          <CoachProfileEditor
            @coach={{@coach}}
            @mode="admin"
            @isReadOnly={{isDisabled}}
            @specialtyOptions={{this.specialtyOptions}}
            @positioningOptions={{this.positioningOptions}}
            @photoDraft={{this.photoDraft}}
            @onPhotoDraftChange={{this.setPhotoDraft}}
            @onPhotoEditSessionChange={{this.setPhotoEditSession}}
          />
        </div>
      </:form>
    </EditRecordSidebar>
  </template>
}
